import React, { useEffect, useState } from 'react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography} from '@mui/material'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable
} from '@tanstack/react-table'

import { CustomerStatementTableDataProps } from './CustomerStatement'

const columnHelper = createColumnHelper<CustomerStatementTableDataProps>()

// Define columns
const columns = [
  columnHelper.accessor('company_name', {
    header: 'Company Name',
    cell: (info) => (
      <Typography variant='body2' fontWeight='medium'>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('order_id', {
    header: 'Order ID',
    cell: (info) => (
      <Typography variant='body2' fontFamily='monospace'>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('product', {
    header: 'Product',
    cell: (info) => (
      <Typography variant='body2' fontWeight={600}>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('currency', {
    header: 'Currency',
    cell: (info) => (
      <Typography variant='body2' fontWeight={600}>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('order_amount', {
    header: 'Order Amount',
    cell: (info) => (
      <Typography variant='body2' fontWeight={600}>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('paid_amount', {
    header: 'Paid Amount',
    cell: (info) => (
      <Typography variant='body2' fontWeight={600}>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('pending_amount', {
    header: 'Pending Amount',
    cell: (info) => (
      <Typography variant='body2' fontWeight={600}>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('payment_mode', {
    header: 'Payment Mode',
    cell: (info) => (
      <Typography variant='body2' fontWeight={600}>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('order_status', {
    header: 'Order Status',
    cell: (info) => (
      <Typography variant='body2' fontWeight={600}>
        {info.getValue()}
      </Typography>
    )
  }),
  // columnHelper.accessor('status', {
  //   header: 'Status',
  //   cell: (info) => <StatusChip status={info.getValue()} />
  // }),
  columnHelper.accessor('order_date', {
    header: 'Order Date',
    cell: (info) => (
      <Typography variant='body2'>
        {new Date(info.getValue()).toLocaleDateString()}
      </Typography>
    )
  })
]

export default function CustomerStatementList({
  data
}: {
  data: CustomerStatementTableDataProps[]
}) {
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 })

  const table = useReactTable({
    data,
    columns,
    state: { pagination },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel() // <-- enables client-side pagination
    // initialState: { pagination: { pageIndex: 0, pageSize: 10 } }, // optional
    // If you add filtering/sorting later, also include getFilteredRowModel/getSortedRowModel
  })

  const { pageIndex, pageSize } = table.getState().pagination

  // Keep page in range if data changes (e.g., filters applied externally)
  const totalRows = table.getPrePaginationRowModel().rows.length
  useEffect(() => {
    const maxPage = Math.max(Math.ceil(totalRows / pageSize) - 1, 0)
    if (pageIndex > maxPage) table.setPageIndex(maxPage)
  }, [pageIndex, pageSize, totalRows, table])

  const paginatedRowCount = table.getFilteredRowModel
    ? table.getFilteredRowModel().rows.length
    : totalRows

  return (
    <Box sx={{ p: 3 }}>
      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: 'grey.50',
                      py: 2
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>

          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                hover
                sx={{
                  '&:nth-of-type(odd)': { backgroundColor: 'action.hover' }
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} sx={{ py: 2.5 }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Empty state */}
        {table?.getRowModel().rows.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color='text.secondary'>No Statement found</Typography>
          </Box>
        )}

        {/* Pagination controls */}
        <TablePagination
          component='div'
          count={paginatedRowCount}
          page={pageIndex}
          onPageChange={(_evt, newPage) => table.setPageIndex(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(evt) => {
            const newSize = parseInt(evt.target.value, 10)
            table.setPageSize(newSize)
            table.setPageIndex(0)
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          showFirstButton
          showLastButton
        />
      </TableContainer>
    </Box>
  )
}
